import React, { useEffect, useState } from "react";
import {
  Box,
  AppBar,
  Grid,
  Breadcrumbs,
  Link,
  Chip,
  CircularProgress,
  Alert,
  Typography,
  Divider,
  Paper,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,Tooltip
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import Navbar from "../../components/common/Navbar";
import { useStyles } from "../../assets/styles/Theme";
import { externalgetApi, externalpostApi } from "../../services/external/externalApis";
import { ORDER_API_15, ORDER_API_18 } from "../../api/ApiModel";
import {
  CON_ORTC_EXCHANGE,
  CON_STATUS_ACTIVE,
  CON_STATUS_RETURN,
  CON_STATUS_YES,
} from "../../utils/sys/Constants";
import {
  getNoteRefundStatus,
  isNoteClaimed,
  isNoteExpired,
} from "../../utils/order/noteRefundStatus";
import { useSelector } from "react-redux";

// ── helpers ───────────────────────────────────────────────────────────────────

const safe = (val) => {
  if (val == null) return "—";
  if (typeof val === "object") return "—";
  const s = String(val).trim();
  return s === "" ? "—" : s;
};

const safeNum = (val) => {
  const n = parseFloat(val);
  return isNaN(n) ? "—" : new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

/** Display API date values as yyyy-MM-dd */
const formatDateYmd = (val) => {
  if (val == null || String(val).trim() === "") return "—";
  const d = dayjs(val);
  return d.isValid() ? d.format("YYYY-MM-DD") : String(val).trim();
};

const getNoteTypeChip = (returnTypeCode, description) => {
  const code = Number(returnTypeCode);
  if (code === CON_ORTC_EXCHANGE)
    return <Chip label={description || "Exchange Note"} color="warning" size="small" />;
  return <Chip label={description || "Credit Note"} color="primary" size="small" />;
};

const getRefundChip = (record) => {
  const status = getNoteRefundStatus(record);
  return <Chip label={status.label} color={status.color} size="small" />;
};

const InfoRow = ({ label, value, theme }) => {
  const remark = safe(value);

  return (
    <Box sx={{ display: "flex", gap: 1, py: 0.5 }}>
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          minWidth: 150,
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>

     {label === "Remark" ? (
  <Box sx={{ flex: 1, minWidth: 0 }}>
  
      <Typography
        variant="body2"
        sx={{
         color: theme.palette.text.primary,
            fontWeight: 600,
      
          display: "-webkit-box",
          WebkitLineClamp: 4,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "normal",
          overflowWrap: "anywhere",
        }}
      >
        {remark}
      </Typography>
   
  </Box>
) : (
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 600,
          }}
        >
          {safe(value)}
        </Typography>
      )}
    </Box>
  );
};

// ── component ─────────────────────────────────────────────────────────────────

function NoteView() {
  const theme    = useTheme();
  const styles   = useStyles(theme);
  const navigate = useNavigate();
  const location = useLocation();

  const { noteRecord } = location.state || {};

  const SYS_USER = useSelector((state) => state.user) || {};
  const { USERNAME, COMPNAME, RESTNAME, ADDRESS } = SYS_USER.USER_PARAM || {};

  const [returnHeader, setReturnHeader] = useState(null);
  const [lineItems,    setLineItems]    = useState([]);
  const [orderItems,   setOrderItems]   = useState([]);
  const [remarkMap,    setRemarkMap]    = useState({});
  const [isLoading,    setIsLoading]    = useState(true);
  const [error,        setError]        = useState(null);

  // Refund dialog
  const [openRefundDialog, setOpenRefundDialog] = useState(false);
  const [refunding,        setRefunding]        = useState(false);
  const [snackbar,         setSnackbar]         = useState({ open: false, message: "", severity: "success" });

  // ── Fetch detail ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!noteRecord?.ORDERCODE || !noteRecord?.ODRETURNCODE) {
      setIsLoading(false);
      return;
    }

    const fetchDetails = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await externalgetApi(ORDER_API_15, "get", {
          ORDERCODE:    noteRecord.ORDERCODE,
          ODRETURNCODE: noteRecord.ODRETURNCODE,
        });

        const rawItems   = result["1"] || [];
        setOrderItems(Array.isArray(rawItems) ? rawItems : Object.values(rawItems).flat().filter(Boolean));

        const rawLines   = result["2"] || [];
        setLineItems(Array.isArray(rawLines) ? rawLines : []);

        const rawRemarks = result["3"] || [];
        const rMap = {};
        (Array.isArray(rawRemarks) ? rawRemarks : []).forEach((r) => { rMap[r.ODREMARKCODE] = r.REMARK; });
        setRemarkMap(rMap);

        const rawHeader  = result["4"] || [];
        setReturnHeader(Array.isArray(rawHeader) ? rawHeader[0] || null : null);
      } catch (err) {
        setError(err?.message || "Failed to load note details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, [noteRecord?.ORDERCODE, noteRecord?.ODRETURNCODE]);

  if (!noteRecord) {
    return (
      <>
        <Navbar />
        <Box sx={{ p: 4 }}>
          <Alert severity="warning">No note record found. Please go back and select a record.</Alert>
        </Box>
      </>
    );
  }

  const hdr = returnHeader || noteRecord;
  const isRefundable =
    !isNoteClaimed(hdr.REFUNDSTS) &&
    !isNoteExpired(hdr.RETURNDATE || noteRecord.RETURNDATE);

  const noteTypeCode = Number(hdr.RETURNTYPECODE || noteRecord.RETURNTYPECODE);
  const actionText   = noteTypeCode === CON_ORTC_EXCHANGE ? "Exchange" : "Return";
  const canRefund = noteTypeCode !== CON_ORTC_EXCHANGE;
  const refundTotalAmount =
    parseFloat(hdr.REFUNDAMOUNT) ||
    lineItems.reduce((sum, line) => sum + (parseFloat(line.AMOUNT) || 0), 0);
  const noteNo = safe(hdr.RETURNNO || hdr.ODRETURNCODE || noteRecord.RETURNNO || noteRecord.ODRETURNCODE);

  // ── Refund handler ──────────────────────────────────────────────────────────
  const handleRefund = async () => {
    setOpenRefundDialog(false);
    setRefunding(true);
    try {
      const today = new Date();
      const pad   = (n) => String(n).padStart(2, "0");
      const refundDate = `${pad(today.getMonth() + 1)}-${pad(today.getDate())}-${today.getFullYear()}`;

      await externalpostApi(ORDER_API_18, {
        ORDERCODE:    Number(hdr.ORDERCODE    || noteRecord.ORDERCODE),
        ODRETURNCODE: Number(hdr.ODRETURNCODE || noteRecord.ODRETURNCODE),
        REFUNDDATE:   refundDate,
        ISREFUND:     CON_STATUS_YES,
        REFUNDSTS:    CON_STATUS_ACTIVE,
        ENUSER:       USERNAME,
        STS:          CON_STATUS_RETURN,
      });
      setSnackbar({ open: true, message: "Refund processed successfully.", severity: "success" });
      setReturnHeader((prev) => ({
        ...(prev || hdr),
        REFUNDSTS: CON_STATUS_ACTIVE,
        ISREFUND:  CON_STATUS_YES,
      }));
    } catch (err) {
      setSnackbar({ open: true, message: err?.message || "Failed to process refund.", severity: "error" });
    } finally {
      setRefunding(false);
    }
  };



  return (
    <>
      <Navbar />

      {/* ── AppBar ── */}
      <AppBar sx={styles.AI_CustomAppBar1}>
        <Box sx={styles.AI_HeaderContainer1}>
          <Grid container alignItems="center">
            <Grid item xs={12} md={8}>
              <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={styles.AI_breadcrumbs2}>
                <Link underline="none">Home</Link>
                <Link underline="none" sx={{ cursor: "pointer" }} onClick={() => navigate("/notelist")}>Returns</Link>
                <Link underline="none" sx={{ cursor: "pointer" }} onClick={() => navigate("/notelist")}>Note List</Link>
                <Link underline="none" color="text.primary">Note View</Link>
              </Breadcrumbs>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" justifyContent="flex-end" spacing={1} sx={styles.AI_fixedStack}>
                {!isLoading && canRefund && isRefundable && (
                  <Button
                    variant="contained"
                    startIcon={<CurrencyExchangeIcon />}
                    disabled={refunding}
                    onClick={() => setOpenRefundDialog(true)}
                    sx={{
                      background: theme.palette.success.main,
                      color: "#fff",
                      textTransform: "capitalize",
                    }}
                  >
                    {refunding ? "Processing…" : "Refund"}
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate("/notelist")}
                  sx={{ background: theme.palette.primary.main, color: theme.palette.text.buttontxt, textTransform: "capitalize" }}
                >
                  Back
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </AppBar>

      <Box sx={styles.AI_AddStyledBox}>
        {/* ── Title bar ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2,
            py: 1.5,
            mt: { xs: "170px", md: "155px" },
            background: theme.palette.background.paper,
            borderBottom: `1px solid ${theme.palette.stroke.main}`,
          }}
        >
          <ReceiptLongOutlinedIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
            {noteTypeCode === CON_ORTC_EXCHANGE ? "Exchange Note" : "Credit Note"}
          </Typography>
          <Chip
            label={`# ${safe(hdr.RETURNNO || noteRecord.RETURNNO)}`}
            size="small"
            sx={{ backgroundColor: `${theme.palette.primary.main}18`, color: theme.palette.primary.main, fontWeight: 600, fontSize: "0.8rem" }}
          />
          <Box sx={{ ml: "auto", display: "flex", gap: 1, alignItems: "center" }}>
            {getNoteTypeChip(hdr.RETURNTYPECODE || noteRecord.RETURNTYPECODE, noteRecord.RETURNTYPEDESCRIPTION)}
            {getRefundChip({
              REFUNDSTS: hdr.REFUNDSTS,
              RETURNDATE: hdr.RETURNDATE || noteRecord.RETURNDATE,
            })}
          </Box>
        </Box>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2 }}><Alert severity="error">{error}</Alert></Box>
        ) : (
          <Box sx={{ p: { xs: 1.5, md: 2 }, display: "flex", flexDirection: "column", gap: 2 }}>

            {/* ── Info cards ── */}
            <Grid container spacing={2}>
              {/* Note Info */}
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2, border: `1px solid ${theme.palette.stroke.main}`, borderRadius: 2, height: "100%" }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 1.5, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.7rem" }}>
                    Note Information
                  </Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  {/* <InfoRow label="Note No"       value={hdr.ODRETURNCODE}  theme={theme} /> */}
                  <InfoRow label="Note No"    value={hdr.RETURNNO}      theme={theme} />
                  <InfoRow label="Date"          value={formatDateYmd(hdr.RETURNDATE)} theme={theme} />
                  <InfoRow label="Amount"        value={safeNum(hdr.REFUNDAMOUNT)} theme={theme} />
                  <InfoRow label="Remark"        value={hdr.REMARK}        theme={theme} />
                  <InfoRow label="Entered By"    value={hdr.ENUSER}        theme={theme} />
                </Paper>
              </Grid>

              {/* Order Info */}
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2, border: `1px solid ${theme.palette.stroke.main}`, borderRadius: 2, height: "100%" }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 1.5, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.7rem" }}>
                    Order Information
                  </Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  {/* <InfoRow label="Order No"      value={hdr.ORDERCODE}            theme={theme} /> */}
                  <InfoRow label="Order Code"    value={noteRecord.INVNO}         theme={theme} />
                  <InfoRow
                    label="Order Date"
                    value={formatDateYmd(noteRecord.ORDERENDATE || hdr.ORDERENDATE)}
                    theme={theme}
                  />
                  {/* <InfoRow label="Delivery Date" value={noteRecord.DELIVERYDATE}  theme={theme} /> */}
                  {/* <InfoRow label="Officer"       value={noteRecord.OFFICERNAME}   theme={theme} /> */}
                </Paper>
              </Grid>
            </Grid>

            {/* ── Items table ── */}
            <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.stroke.main}`, borderRadius: 2, overflow: "hidden" }}>
              <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.stroke.main}`, background: theme.palette.background.default, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.primary.main, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.7rem" }}>
                  Items
                </Typography>
              </Box>

              {lineItems.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>No items found.</Typography>
                </Box>
              ) : (
                <Box sx={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <colgroup>
                      <col style={{ width: "40px"  }} />
                      <col style={{ width: "28%"   }} />
                      <col style={{ width: "60px"  }} />
                      <col style={{ width: "110px" }} />
                      <col style={{ width: "110px" }} />
                      <col />
                    </colgroup>
                    <thead>
                      <tr style={{ background: theme.palette.background.tertiary }}>
                        {["#", "Item", "Qty", "Rate", "Amount", "Remark"].map((h) => (
                          <th key={h} style={{ padding: "8px", textAlign: h === "Rate" || h === "Amount" ? "right" : h === "Qty" ? "center" : "left", fontSize: "0.75rem", fontWeight: 700, color: theme.palette.text.secondary, borderBottom: `1px solid ${theme.palette.stroke.main}`, whiteSpace: "nowrap" }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((line, idx) => {
                        const oi = orderItems.find((i) => String(i.IDNO) === String(line.ORDERITEMCODE));
                        const menuName = oi?.MENUNAME || safe(line.MENUCODE);
                        const remark   = remarkMap[line.ODREMARKCODE] || line.REMARK;
                        return (
                          <tr key={idx} style={{ borderBottom: `1px solid ${theme.palette.stroke.main}` }}>
                            <td style={{ padding: "7px 8px", fontSize: "0.8rem",  color: theme.palette.text.secondary, textAlign: "left"   }}>{idx + 1}</td>
                            <td style={{ padding: "7px 8px", fontSize: "0.85rem", fontWeight: 600, textAlign: "left",   overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{safe(menuName)}</td>
                            <td style={{ padding: "7px 8px", fontSize: "0.85rem", textAlign: "center" }}>{safe(line.QTY)}</td>
                            <td style={{ padding: "7px 8px", fontSize: "0.85rem", textAlign: "right"  }}>{safeNum(line.RATE)}</td>
                            <td style={{ padding: "7px 8px", fontSize: "0.85rem", fontWeight: 700, textAlign: "right", color: theme.palette.primary.main }}>{safeNum(line.AMOUNT)}</td>
                            <td style={{ padding: "7px 8px", fontSize: "0.8rem",  color: theme.palette.text.secondary, textAlign: "left",   overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{safe(remark)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>
              )}

              {/* Totals */}
              {lineItems.length > 0 && (
                <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${theme.palette.stroke.main}`, background: theme.palette.background.default, display: "flex", justifyContent: "flex-end", gap: 4 }}>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Total Qty</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {lineItems.reduce((s, i) => s + (parseFloat(i.QTY) || 0), 0)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Total Amount</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                      {safeNum(lineItems.reduce((s, i) => s + (parseFloat(i.AMOUNT) || 0), 0))}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>

          </Box>
        )}
      </Box>

      {/* ── Refund confirmation dialog ── */}
      <Dialog
        open={openRefundDialog}
        onClose={() => setOpenRefundDialog(false)}
        aria-labelledby="note-refund-confirmation-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="note-refund-confirmation-dialog-title" sx={{ pb: 1 }}>
          Confirm Refund Order
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, mb: 1 }}
          >
            Process a refund of{" "}
            <strong style={{ color: theme.palette.primary.main }}>
              {safeNum(refundTotalAmount)}
            </strong>{" "}
            for note no. <strong>{noteNo}</strong>?
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary }}
          >
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenRefundDialog(false)}
            variant="outlined"
            sx={{ textTransform: "capitalize", borderRadius: 2, px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRefund}
            variant="contained"
            disabled={refunding}
            sx={{
              background: theme.palette.primary.main,
              color: theme.palette.text.buttontxt,
              textTransform: "capitalize",
              borderRadius: 2,
              px: 3,
              "&:hover": {
                background: theme.palette.primary.main,
                opacity: 0.9,
              },
            }}
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default NoteView;
